/** biome-ignore-all lint/suspicious/noExplicitAny: <explanation> */

import { getLocalTimeZone, parseDate, today } from "@internationalized/date";
import type { Metric } from "@unbox/api";
import { Calendar } from "lucide-react";
import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDateLimits } from "@/api/date-limits";
import { DateRangePicker as DateRangeSelector } from "@/components/application/date-picker/date-range-picker";
import { Button } from "@/components/base/buttons/button";
import { DateGranularitySelector } from "@/components/DateGranularitySelector";
import { MetricsBarChartView } from "@/components/MetricsBarChartView";
import { MetricsLineChartView } from "@/components/MetricsLineChartView";
import { CURRENCY_METRICS, METRIC_COLORS, METRICS_MAP, PERCENT_METRICS } from "@/constants/metrics";
import { useReportFormatters } from "@/hooks/use-report-formatters";
import { useProcessedMetricsData } from "../hooks/use-processed-metrics-data";
import { useReportDateFilter } from "../hooks/use-report-date-filter";

// Chart PDF export helper function
export const exportChartToPDF = async (chartElement: HTMLElement, reportName: string) => {
  try {
    // Dynamically import the libraries to avoid bundle size issues
    const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([import("html2canvas"), import("jspdf")]);

    // Hide elements marked for no-export
    const elementsToHide = chartElement.querySelectorAll("[data-no-export]");
    const originalDisplays: string[] = [];

    elementsToHide.forEach((element, index) => {
      const htmlElement = element as HTMLElement;
      originalDisplays[index] = htmlElement.style.display;
      htmlElement.style.display = "none";
    });

    // Capture the chart as canvas
    const canvas = await html2canvas(chartElement, {
      backgroundColor: "#ffffff",
      scale: 2, // Higher quality
      logging: false,
      allowTaint: false,
      useCORS: true,
    });

    // Restore original display values
    elementsToHide.forEach((element, index) => {
      const htmlElement = element as HTMLElement;
      htmlElement.style.display = originalDisplays[index];
    });

    // Create PDF
    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    // Add title at the top
    pdf.setFontSize(16);
    pdf.setFont("helvetica", "bold");
    pdf.text(reportName, 15, 20);

    // Add timestamp below title
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "normal");
    const timestamp =
      new Date().toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }) +
      ", " +
      new Date().toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    pdf.text(`Generated on: ${timestamp}`, 15, 28);

    // Calculate dimensions to fit the page (leaving space for title)
    const availableHeight = 160; // A4 landscape height minus margins and title space
    const imgWidth = 267; // A4 landscape width minus margins
    const imgHeight = Math.min((canvas.height * imgWidth) / canvas.width, availableHeight);

    // Add the chart image to PDF (positioned below title)
    pdf.addImage(
      canvas.toDataURL("image/png"),
      "PNG",
      15, // x position
      40, // y position (below title and timestamp)
      imgWidth,
      imgHeight,
    );

    // Save the PDF
    pdf.save(`${reportName.replace(/[^a-z0-9]/gi, "_")}_chart.pdf`);
  } catch (error) {
    console.error("Error exporting chart to PDF:", error);
    throw new Error("Failed to export chart to PDF");
  }
};

export const ChartReport: React.FC<{
  metrics: Metric[];
  reportId: string;
  createdAt: string;
  updatedAt: string;
  onExport?: (data: any[], metrics: string[], reportName: string) => void;
  reportName: string;
  fixedStartDate?: string;
  fixedEndDate?: string;
}> = ({ metrics, reportId, createdAt, updatedAt, onExport, reportName, fixedStartDate, fixedEndDate }) => {
  const { dateRangeMode, setDateRangeMode, dateRange, setDateRange, timePeriod, hasFixedDates } = useReportDateFilter(
    reportId,
    "chart",
    fixedStartDate,
    fixedEndDate,
  );
  const [chartType] = useState<"area" | "line">("area");
  const chartRef = useRef<HTMLDivElement>(null);

  // Date limits for restricting selectable dates
  const { data: dateLimits } = useDateLimits();
  const limits = useMemo(() => {
    if (!dateLimits) return undefined;
    return {
      minDate: parseDate(dateLimits.minDate),
      maxDate: parseDate(dateLimits.maxDate),
    };
  }, [dateLimits]);

  // Use common data processing hook (with gap filling for fixed date reports)
  const { filteredData: chartData } = useProcessedMetricsData(metrics, timePeriod, dateRange, hasFixedDates);

  // Build chart config
  const { gmv: gmvFormatter, video: videoFormatter, percent: percentFormatter } = useReportFormatters();

  const chartConfig = useMemo(() => {
    return {
      metrics: metrics,
      formatters: Object.fromEntries(
        metrics.map((m) => [
          m,
          CURRENCY_METRICS.includes(m)
            ? (v: number) => gmvFormatter.format(v)
            : PERCENT_METRICS.includes(m)
              ? (v: number) => percentFormatter.format(v)
              : (v: number) => videoFormatter.format(v),
        ]),
      ),
      colors: Object.fromEntries(metrics.map((m) => [m, METRIC_COLORS[m] || "#6b7280"])),
      labels: Object.fromEntries(metrics.map((m) => [m, METRICS_MAP[m]?.label || m])),
    };
  }, [metrics, gmvFormatter, videoFormatter, percentFormatter]);

  // Update parent with current data whenever it changes
  useEffect(() => {
    if (onExport && chartData.length > 0) {
      onExport(chartData, metrics, reportName);
    }
  }, [onExport, chartData, metrics, reportName]);

  return (
    <div className="flex flex-col h-full gap-4">
      <div ref={chartRef} className="flex flex-col flex-1 min-h-0 bg-white border border-gray-200 rounded-lg p-6 gap-4">
        {/* Controls Section - Legend on left, Date controls on right */}
        <div className="flex items-center justify-between flex-shrink-0" data-no-export>
          {/* Legend on the left */}
          <div className="flex items-center gap-6">
            {metrics.map((metric) => (
              <div key={metric} className="flex items-center gap-2">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: METRIC_COLORS[metric] || "#6b7280" }}
                ></div>
                <span className="text-sm font-medium text-gray-900">{METRICS_MAP[metric]?.label || metric}</span>
              </div>
            ))}
          </div>

          {/* Date controls on the right */}
          <div className="flex items-center gap-3">
            {hasFixedDates && dateRange ? (
              /* Fixed date range display (read-only) */
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Calendar className="w-4 h-4" />
                <span>
                  {dateRange.start.toDate(getLocalTimeZone()).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}{" "}
                  -{" "}
                  {dateRange.end.toDate(getLocalTimeZone()).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </span>
              </div>
            ) : (
              <>
                {/* Date Range Selector */}
                <DateRangeSelector
                  value={dateRange}
                  onChange={setDateRange}
                  selected={dateRange ? { start: dateRange.start, end: dateRange.end } : null}
                  isActive={!!dateRange}
                  textClassName="text-sm font-semibold"
                  buttonClassName="h-10"
                  aria-label="Date range picker"
                  shouldCloseOnSelect={false}
                  onApply={() => {
                    // Date range is already set via onChange
                  }}
                  onReset={() => {
                    // Default to last 31 days
                    const now = today(getLocalTimeZone());
                    const thirtyOneDaysAgo = now.subtract({ days: 31 });
                    setDateRange({ start: thirtyOneDaysAgo, end: now });
                  }}
                  showPresets={true}
                  resetText="Last 31 days"
                  limits={limits}
                />
              </>
            )}

            {/* Time Step Pills */}
            <DateGranularitySelector value={dateRangeMode} onChange={setDateRangeMode} />
          </div>
        </div>

        {/* Chart Content */}
        <div className="h-[60dvh] flex-col min-h-0">
          <MetricsLineChartView
            data={chartData as any}
            config={chartConfig as any}
            chartType={chartType}
            showLegend={false}
          />
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between text-sm text-gray-500 flex-shrink-0" data-no-export>
        <div>
          Last edited:{" "}
          {new Date(updatedAt).toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "2-digit",
          }) +
            " @" +
            new Date(updatedAt).toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            })}
        </div>
        <div>
          Created:{" "}
          {new Date(createdAt).toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "2-digit",
          })}
        </div>
      </div>
    </div>
  );
};
